import React, { useEffect, useState } from 'react';
import { CreditCard, Plus, Edit, RefreshCw, Copy } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Modal, Input, Label, Spinner, Switch, Column } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  getAdminPlans,
  updatePlan,
  createPlan,
  syncPlanToStripe,
  type PlanRecord,
  type CreatePlanBody,
  type UpdatePlanBody,
} from '@/services/billingService';

type ReportTier = 'basic' | 'advanced' | 'unlimited';
type SupportTier = 'community' | 'standard' | 'priority' | 'dedicated';

/** Editable form state — flattened from PlanDefinition for the dialogs. */
interface PlanFormState {
  id: string;
  displayName: string;
  description: string;
  public: boolean;
  archived: boolean;
  order: string;
  customForOrgId: string;
  // pricing
  basePriceUsd: string;
  perDevicePriceUsd: string;
  // entitlements
  tokensPerMonth: string;
  tokensUnlimited: boolean;
  ownDeviceTokenRate: string;
  maxUsers: string;
  apiAccess: boolean;
  aiAssistant: boolean;
  schedulesEnabled: boolean;
  reportTier: ReportTier;
  supportTier: SupportTier;
  myDevicesEnabled: boolean;
  includedDevices: string;
  maxDevices: string;
}

const EMPTY_FORM: PlanFormState = {
  id: '',
  displayName: '',
  description: '',
  public: false,
  archived: false,
  order: '0',
  customForOrgId: '',
  basePriceUsd: '0',
  perDevicePriceUsd: '',
  tokensPerMonth: '0',
  tokensUnlimited: false,
  ownDeviceTokenRate: '1',
  maxUsers: '0',
  apiAccess: true,
  aiAssistant: true,
  schedulesEnabled: true,
  reportTier: 'basic',
  supportTier: 'standard',
  myDevicesEnabled: false,
  includedDevices: '0',
  maxDevices: '',
};

function formFromPlan(plan: PlanRecord): PlanFormState {
  const e = plan.entitlements;
  const p = plan.pricing;
  return {
    id: plan.id,
    displayName: plan.displayName ?? '',
    description: plan.description ?? '',
    public: !!plan.public,
    archived: !!plan.archived,
    order: String((plan as any).order ?? 0),
    customForOrgId: plan.customForOrgId ?? '',
    basePriceUsd: String(p?.basePriceUsd ?? 0),
    perDevicePriceUsd: p?.perDevicePriceUsd != null ? String(p.perDevicePriceUsd) : '',
    tokensPerMonth: String(e?.tokensPerMonth ?? 0),
    tokensUnlimited: !!e?.tokensUnlimited,
    ownDeviceTokenRate: String(e?.ownDeviceTokenRate ?? 1),
    maxUsers: String(e?.maxUsers ?? 0),
    apiAccess: !!e?.apiAccess,
    aiAssistant: !!e?.aiAssistant,
    schedulesEnabled: !!e?.schedulesEnabled,
    reportTier: (e?.reportTier ?? 'basic') as ReportTier,
    supportTier: (e?.supportTier ?? 'standard') as SupportTier,
    myDevicesEnabled: !!e?.myDevices?.enabled,
    includedDevices: String(e?.myDevices?.includedDevices ?? 0),
    maxDevices: e?.myDevices?.maxDevices != null ? String(e.myDevices.maxDevices) : '',
  };
}

function num(v: string, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Build the pricing + entitlements payload shared by create and update. */
function bodyFromForm(form: PlanFormState): UpdatePlanBody {
  const pricing: NonNullable<UpdatePlanBody['pricing']> = {
    basePriceUsd: num(form.basePriceUsd),
  };
  if (form.perDevicePriceUsd.trim() !== '') pricing.perDevicePriceUsd = num(form.perDevicePriceUsd);

  const entitlements: NonNullable<UpdatePlanBody['entitlements']> = {
    tokensPerMonth: num(form.tokensPerMonth),
    tokensUnlimited: form.tokensUnlimited,
    ownDeviceTokenRate: num(form.ownDeviceTokenRate),
    maxUsers: num(form.maxUsers),
    apiAccess: form.apiAccess,
    aiAssistant: form.aiAssistant,
    schedulesEnabled: form.schedulesEnabled,
    reportTier: form.reportTier,
    supportTier: form.supportTier,
    myDevices: {
      enabled: form.myDevicesEnabled,
      includedDevices: num(form.includedDevices),
      ...(form.maxDevices.trim() !== '' ? { maxDevices: num(form.maxDevices) } : {}),
    },
  };

  return {
    displayName: form.displayName.trim(),
    description: form.description.trim(),
    public: form.public,
    archived: form.archived,
    order: num(form.order),
    pricing,
    entitlements,
  };
}

const SYNC_BADGE: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'secondary' }> = {
  synced: { label: 'Synced', variant: 'success' },
  dirty: { label: 'Dirty', variant: 'warning' },
  error: { label: 'Error', variant: 'error' },
};

export function SubscriptionPlansPage() {
  const { isSuperAdmin } = useAuth();
  const { success, error: showError, info } = useToast();

  const [plans, setPlans] = useState<PlanRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit dialog
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<PlanFormState>(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Create dialog
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<PlanFormState>(EMPTY_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Clone dialog
  const [cloneSource, setCloneSource] = useState<PlanRecord | null>(null);
  const [cloneId, setCloneId] = useState('');
  const [cloneCustomOrgId, setCloneCustomOrgId] = useState('');
  const [cloneSaving, setCloneSaving] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Per-row sync spinner
  const [syncingId, setSyncingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadPlans();
  }, [isSuperAdmin]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const fetched = await getAdminPlans();
      setPlans(fetched);
    } catch (err: any) {
      console.error('Failed to load subscription plans:', err);
      showError(err?.message ?? 'Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (plan: PlanRecord) => {
    setEditForm(formFromPlan(plan));
    setEditError(null);
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.displayName.trim()) {
      setEditError('Display name is required');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updatePlan(editForm.id, bodyFromForm(editForm));
      success(`Plan "${editForm.displayName}" updated`);
      setIsEditOpen(false);
      await loadPlans();
    } catch (err: any) {
      console.error('Failed to update plan:', err);
      setEditError(err?.message ?? 'Failed to update plan');
    } finally {
      setEditSaving(false);
    }
  };

  const openCreate = () => {
    setCreateForm(EMPTY_FORM);
    setCreateError(null);
    setIsCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.id.trim()) {
      setCreateError('Plan id is required');
      return;
    }
    if (!createForm.displayName.trim()) {
      setCreateError('Display name is required');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const body: CreatePlanBody = {
        id: createForm.id.trim(),
        ...(createForm.customForOrgId.trim() ? { customForOrgId: createForm.customForOrgId.trim() } : {}),
        ...bodyFromForm(createForm),
      };
      await createPlan(body);
      success(`Plan "${createForm.displayName}" created`);
      setIsCreateOpen(false);
      await loadPlans();
    } catch (err: any) {
      console.error('Failed to create plan:', err);
      setCreateError(err?.message ?? 'Failed to create plan');
    } finally {
      setCreateSaving(false);
    }
  };

  const openClone = (plan: PlanRecord) => {
    setCloneSource(plan);
    setCloneId('');
    setCloneCustomOrgId('');
    setCloneError(null);
  };

  const handleClone = async () => {
    if (!cloneSource) return;
    if (!cloneId.trim()) {
      setCloneError('New plan id is required');
      return;
    }
    setCloneSaving(true);
    setCloneError(null);
    try {
      await createPlan({
        id: cloneId.trim(),
        cloneFrom: cloneSource.id,
        ...(cloneCustomOrgId.trim() ? { customForOrgId: cloneCustomOrgId.trim() } : {}),
      });
      success(`Cloned "${cloneSource.displayName}" → "${cloneId.trim()}"`);
      setCloneSource(null);
      await loadPlans();
    } catch (err: any) {
      console.error('Failed to clone plan:', err);
      setCloneError(err?.message ?? 'Failed to clone plan');
    } finally {
      setCloneSaving(false);
    }
  };

  const handleSync = async (plan: PlanRecord) => {
    setSyncingId(plan.id);
    try {
      const res = await syncPlanToStripe(plan.id);
      // Phase D: endpoint returns 501; on the off chance it succeeds, treat as success.
      success(res?.message ?? `Synced "${plan.displayName}" to Stripe`);
      await loadPlans();
    } catch (err: any) {
      // 501 Not Implemented (Phase E) — surface the message as info, not an error.
      info(err?.message ?? 'Stripe catalog sync ships in Phase E.');
    } finally {
      setSyncingId(null);
    }
  };

  const columns: Column<PlanRecord>[] = [
    {
      key: 'displayName',
      header: 'Plan',
      sortable: true,
      render: (_v, plan) => (
        <div>
          <div className="font-medium text-foreground">{plan.displayName}</div>
          <div className="text-xs text-muted-foreground font-mono">{plan.id}</div>
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      sortable: true,
      sortValue: (plan) => plan.pricing?.basePriceUsd ?? 0,
      render: (_v, plan) => {
        const base = plan.pricing?.basePriceUsd ?? 0;
        if (base === 0 && plan.id === 'enterprise') {
          return <span className="text-foreground">Custom</span>;
        }
        return <span className="text-foreground">${base.toLocaleString()}/mo</span>;
      },
    },
    {
      key: 'tokens',
      header: 'Tokens',
      render: (_v, plan) => (
        <span className="text-foreground">
          {plan.entitlements?.tokensUnlimited ? '∞' : (plan.entitlements?.tokensPerMonth ?? 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'devices',
      header: 'Devices',
      render: (_v, plan) => {
        const md = plan.entitlements?.myDevices;
        if (!md?.enabled) return <span className="text-muted-foreground">—</span>;
        return <span className="text-foreground">{md.maxDevices != null ? md.maxDevices : '∞'}</span>;
      },
    },
    {
      key: 'reportTier',
      header: 'Reports',
      render: (_v, plan) => (
        <Badge variant="secondary" className="capitalize">
          {plan.entitlements?.reportTier ?? 'basic'}
        </Badge>
      ),
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (_v, plan) => (
        <div className="flex items-center gap-1.5 flex-wrap">
          {plan.public ? (
            <Badge variant="success">Public</Badge>
          ) : (
            <Badge variant="secondary">Hidden</Badge>
          )}
          {plan.archived && <Badge variant="warning">Archived</Badge>}
          {plan.customForOrgId && <Badge variant="secondary">Custom</Badge>}
        </div>
      ),
    },
    {
      key: 'sync',
      header: 'Stripe',
      render: (_v, plan) => {
        const status = plan.pricing?.syncStatus;
        const cfg = status ? SYNC_BADGE[status] : undefined;
        return cfg ? (
          <Badge variant={cfg.variant}>{cfg.label}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_v, plan) => (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => openEdit(plan)}>
            <Edit className="w-3 h-3" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={syncingId === plan.id}
            onClick={() => handleSync(plan)}
          >
            {syncingId === plan.id ? <Spinner size="sm" /> : <RefreshCw className="w-3 h-3" />}
            Sync
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openClone(plan)}>
            <Copy className="w-3 h-3" />
            Clone
          </Button>
        </div>
      ),
    },
  ];

  if (!isSuperAdmin) return null;

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2 flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Plan Catalog
            </h1>
            <p className="text-body text-muted-foreground">
              Manage subscription plans, entitlements, and pricing across the platform.
            </p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Create plan
          </Button>
        </div>

        <Card className="p-6">
          <DataTable
            columns={columns}
            data={plans}
            keyExtractor={(row) => row.id}
            searchable
            searchPlaceholder="Search plans by name or id..."
            searchKeys={['id']}
            paginated
            defaultPageSize={10}
            emptyMessage="No plans yet"
            loading={loading}
          />
        </Card>
      </div>

      {/* Edit plan */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title={`Edit plan: ${editForm.displayName || editForm.id}`}
        description="Update display, pricing, and entitlements for this plan."
        size="xl"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setIsEditOpen(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={editSaving}>
              {editSaving ? <Spinner size="sm" /> : <Edit className="w-4 h-4" />}
              {editSaving ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        }
      >
        <PlanFormFields form={editForm} setForm={setEditForm} error={editError} />
      </Modal>

      {/* Create plan */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create plan"
        description="Define a new subscription plan."
        size="xl"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={createSaving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createSaving}>
              {createSaving ? <Spinner size="sm" /> : <Plus className="w-4 h-4" />}
              {createSaving ? 'Creating...' : 'Create plan'}
            </Button>
          </div>
        }
      >
        <PlanFormFields form={createForm} setForm={setCreateForm} error={createError} showId />
      </Modal>

      {/* Clone plan */}
      <Modal
        isOpen={cloneSource !== null}
        onClose={() => setCloneSource(null)}
        title={cloneSource ? `Clone plan: ${cloneSource.displayName}` : 'Clone plan'}
        description="Create a new plan that copies this plan's pricing and entitlements."
        size="sm"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => setCloneSource(null)} disabled={cloneSaving}>
              Cancel
            </Button>
            <Button onClick={handleClone} disabled={cloneSaving}>
              {cloneSaving ? <Spinner size="sm" /> : <Copy className="w-4 h-4" />}
              {cloneSaving ? 'Cloning...' : 'Clone plan'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {cloneError && <p className="text-status-error text-sm">{cloneError}</p>}
          <div>
            <Label htmlFor="clone-id">New plan id</Label>
            <Input
              id="clone-id"
              placeholder="custom_acme"
              value={cloneId}
              onChange={(e) => setCloneId(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lowercase identifier for the new plan document.
            </p>
          </div>
          <div>
            <Label htmlFor="clone-org">Custom for organization (optional)</Label>
            <Input
              id="clone-org"
              placeholder="organizationId"
              value={cloneCustomOrgId}
              onChange={(e) => setCloneCustomOrgId(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Set to scope this clone to a single org (enterprise custom plans).
            </p>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}

// ----------------------------------------------------------------------------
// Shared form fields (create + edit)
// ----------------------------------------------------------------------------

interface PlanFormFieldsProps {
  form: PlanFormState;
  setForm: React.Dispatch<React.SetStateAction<PlanFormState>>;
  error: string | null;
  /** Show the plan-id input (create only). */
  showId?: boolean;
}

function SwitchRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div>
        <Label className="text-sm">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

const selectClass =
  'mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent';

function PlanFormFields({ form, setForm, error, showId }: PlanFormFieldsProps) {
  const set = <K extends keyof PlanFormState>(key: K, value: PlanFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6">
      {error && <p className="text-status-error text-sm">{error}</p>}

      {/* General */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">General</h3>
        {showId && (
          <div>
            <Label htmlFor="plan-id">Plan id</Label>
            <Input
              id="plan-id"
              placeholder="starter"
              value={form.id}
              onChange={(e) => set('id', e.target.value)}
              className="mt-1"
            />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="plan-name">Display name</Label>
            <Input
              id="plan-name"
              placeholder="Starter"
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="plan-order">Order</Label>
            <Input
              id="plan-order"
              type="number"
              value={form.order}
              onChange={(e) => set('order', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="plan-desc">Description</Label>
          <Input
            id="plan-desc"
            placeholder="Short marketing description"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SwitchRow
            label="Public"
            hint="Listed on pricing + selectable in checkout."
            checked={form.public}
            onChange={(v) => set('public', v)}
          />
          <SwitchRow
            label="Archived"
            hint="Hidden everywhere but kept for legacy subscribers."
            checked={form.archived}
            onChange={(v) => set('archived', v)}
          />
        </div>
      </div>

      {/* Pricing */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground">Pricing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="plan-base-price">Base price (USD / month)</Label>
            <Input
              id="plan-base-price"
              type="number"
              value={form.basePriceUsd}
              onChange={(e) => set('basePriceUsd', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="plan-device-price">Per-device price (USD)</Label>
            <Input
              id="plan-device-price"
              type="number"
              placeholder="(included)"
              value={form.perDevicePriceUsd}
              onChange={(e) => set('perDevicePriceUsd', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank if devices are included in base.</p>
          </div>
        </div>
      </div>

      {/* Entitlements */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground">Entitlements</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="plan-tokens">Tokens / month</Label>
            <Input
              id="plan-tokens"
              type="number"
              value={form.tokensPerMonth}
              onChange={(e) => set('tokensPerMonth', e.target.value)}
              className="mt-1"
              disabled={form.tokensUnlimited}
            />
          </div>
          <div className="flex items-end pb-1">
            <SwitchRow
              label="Unlimited tokens"
              checked={form.tokensUnlimited}
              onChange={(v) => set('tokensUnlimited', v)}
            />
          </div>
          <div>
            <Label htmlFor="plan-own-rate">Own-device token rate</Label>
            <Input
              id="plan-own-rate"
              type="number"
              step="0.1"
              value={form.ownDeviceTokenRate}
              onChange={(e) => set('ownDeviceTokenRate', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">0 = own-device tests free; 1 = full charge.</p>
          </div>
          <div>
            <Label htmlFor="plan-max-users">Max users</Label>
            <Input
              id="plan-max-users"
              type="number"
              value={form.maxUsers}
              onChange={(e) => set('maxUsers', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">0 = unlimited.</p>
          </div>
          <div>
            <Label htmlFor="plan-report-tier">Report tier</Label>
            <select
              id="plan-report-tier"
              className={selectClass}
              value={form.reportTier}
              onChange={(e) => set('reportTier', e.target.value as ReportTier)}
            >
              <option value="basic">Basic</option>
              <option value="advanced">Advanced</option>
              <option value="unlimited">Unlimited</option>
            </select>
          </div>
          <div>
            <Label htmlFor="plan-support-tier">Support tier</Label>
            <select
              id="plan-support-tier"
              className={selectClass}
              value={form.supportTier}
              onChange={(e) => set('supportTier', e.target.value as SupportTier)}
            >
              <option value="community">Community</option>
              <option value="standard">Standard</option>
              <option value="priority">Priority</option>
              <option value="dedicated">Dedicated</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SwitchRow label="API access" checked={form.apiAccess} onChange={(v) => set('apiAccess', v)} />
          <SwitchRow label="AI assistant" checked={form.aiAssistant} onChange={(v) => set('aiAssistant', v)} />
          <SwitchRow
            label="Schedules enabled"
            checked={form.schedulesEnabled}
            onChange={(v) => set('schedulesEnabled', v)}
          />
        </div>
      </div>

      {/* My Devices */}
      <div className="space-y-4 pt-4 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground">My Devices (private edges)</h3>
        <SwitchRow
          label="My Devices enabled"
          checked={form.myDevicesEnabled}
          onChange={(v) => set('myDevicesEnabled', v)}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="plan-included-devices">Included devices</Label>
            <Input
              id="plan-included-devices"
              type="number"
              value={form.includedDevices}
              onChange={(e) => set('includedDevices', e.target.value)}
              className="mt-1"
              disabled={!form.myDevicesEnabled}
            />
          </div>
          <div>
            <Label htmlFor="plan-max-devices">Max devices</Label>
            <Input
              id="plan-max-devices"
              type="number"
              placeholder="(unlimited)"
              value={form.maxDevices}
              onChange={(e) => set('maxDevices', e.target.value)}
              className="mt-1"
              disabled={!form.myDevicesEnabled}
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank for unlimited.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
